/**
 * Components that render configuration options/settings for each
 * trigger event type.
 *
 * Note: Styling for these components is based on
 * that for CreateTriggerDrawer
 */

import {Select} from '@wandb/weave/components/Form/Select';
import {IconClose} from '@wandb/weave/components/Icon';
import {TooltipDeprecated} from '@wandb/weave/components/TooltipDeprecated';
import React, {useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {
  ArtifactCollectionType,
  TriggerScopeType,
  useProjectArtifactCollectionsQuery,
} from '../../../generated/graphql';
import {type Key as ArtifactKey} from '../../../util/artifacts';
import {propagateErrorsContext} from '../../../util/errors';
import {EMPTY_FILTER_VALUE} from '../../../util/filtersDefaults';
import {type FilterKey, type ValueFilter} from '../../../util/filterTypes';
import {
  EventStoreActions,
  isAliasOrTagEventConfiguredOk,
  type TriggerRootFilter,
  useEventStore,
} from './CreateTriggerDrawer/EventStore';
import {getRegexError} from './CreateTriggerDrawer/regexValidation';
import {ErrorText} from './CreateTriggerDrawer/shared';
import {isScopeFixedEntryPoint} from './CreateTriggerDrawer/types';
import {createArtifactFilter} from './filterUtils';
import * as S from './TriggerEventConfiguration.styles';
import {type OptionType} from './TriggersTable/utilTypes';

type EventConfigurationFilterProps = {
  descriptionString: string;
  index: number;
  filter: ValueFilter<FilterKey>;
  triggerScopeID?: string;
  setFilter(index: number, filter: ValueFilter<FilterKey>): void;
  setTriggerScopeID(val: string): void;
  setTriggerArtifactCollectionName?(val: string): void;
};

export type EventConfigurationProps = {
  filters: TriggerRootFilter['filters'][0];
  artifactCollectionID?: string;
  triggerScopeType?: TriggerScopeType;
  triggerScopeID?: string;
  setFilter(i: number, filter: ValueFilter<FilterKey>): void;
  pushFilter(newFilter: ValueFilter<FilterKey>): void;
  deleteFilter(index: number): void;
  setTriggerScopeType?(val: TriggerScopeType): void;
  setTriggerScopeID(val: string | undefined): void;
  setTriggerArtifactCollectionName?(val: string): void;
};

export const ArtifactAliasFilterCreator = ({
  filters: rootFilters,
  artifactCollectionID,
  setFilter,
  deleteFilter,
  setTriggerScopeType,
  setTriggerScopeID,
  triggerScopeType,
  triggerScopeID,
  setTriggerArtifactCollectionName,
}: EventConfigurationProps) => {
  const {entryPoint, projectID, getAllowArtifactSelection} = useEventStore(
    useShallow(s => s)
  );
  const allowArtifactSelection = getAllowArtifactSelection();
  const isScopeFixed = isScopeFixedEntryPoint(entryPoint);
  const [isShowArtifactFilterEnabled, setIsShowArtifactFilterEnabled] =
    useState(
      allowArtifactSelection &&
        triggerScopeType === TriggerScopeType.ArtifactCollection
    );
  const aliasRegexError = getRegexError(
    (rootFilters.filters[0]?.value as string) ?? ''
  );
  useEffect(() => {
    // The triggerScopeType/triggerScopeID must get set, so
    // when the user is not selecting a collection manually,
    // we need to set it for them.
    // When the scope is fixed (Hub picks it, or an org/team edit carries it),
    // it is set outside this UI and must not be re-derived here.
    if (
      !isScopeFixed &&
      (!isShowArtifactFilterEnabled || !allowArtifactSelection)
    ) {
      if (artifactCollectionID) {
        setTriggerScopeType?.(TriggerScopeType.ArtifactCollection);
        setTriggerScopeID(artifactCollectionID);
      } else {
        setTriggerScopeType?.(TriggerScopeType.Project);
        setTriggerScopeID(projectID);
      }
    }

    const firstFilterValue = rootFilters.filters[0]?.value;
    const firstFilterIsValid =
      firstFilterValue != null &&
      firstFilterValue !== '' &&
      getRegexError(firstFilterValue as string) === undefined;
    EventStoreActions.setIsAddAliasConfigurationOk(
      isAliasOrTagEventConfiguredOk(
        firstFilterIsValid,
        allowArtifactSelection,
        isShowArtifactFilterEnabled,
        triggerScopeID
      )
    );
  }, [
    artifactCollectionID,
    allowArtifactSelection,
    isScopeFixed,
    isShowArtifactFilterEnabled,
    projectID,
    rootFilters.filters,
    setTriggerScopeID,
    setTriggerScopeType,
    triggerScopeID,
  ]);
  return (
    <>
      <S.LabelRow>
        <S.Label>Alias regex</S.Label>
      </S.LabelRow>
      <S.StyledTextField
        placeholder="Name or regex"
        value={(rootFilters.filters[0]?.value as string) ?? ''}
        onChange={value => {
          setFilter(0, createArtifactFilter('alias', value as string));
        }}
        autoFocus={true}
      />
      {aliasRegexError && <ErrorText>{aliasRegexError}</ErrorText>}

      {allowArtifactSelection && (
        <>
          {!isShowArtifactFilterEnabled && rootFilters.filters[0] && (
            <S.AddFilter
              className="fake-link filter__new"
              data-test="filter-add"
              onClick={() => {
                setIsShowArtifactFilterEnabled(true);
                if (setTriggerScopeType != null) {
                  setTriggerScopeType(TriggerScopeType.ArtifactCollection);
                  setTriggerScopeID(artifactCollectionID ?? '');
                }
              }}>
              Add artifact filter
            </S.AddFilter>
          )}
          {isShowArtifactFilterEnabled && (
            <>
              <S.LabelRow>
                <S.Label>Artifact</S.Label>
              </S.LabelRow>
              <S.InlineBlockAction>
                <S.InlineBlockActionLeft>
                  <ArtifactCollectionSelector
                    index={1}
                    descriptionString="Artifact"
                    filter={rootFilters.filters[1] ?? EMPTY_FILTER_VALUE}
                    setFilter={
                      /* TODO: it seems like this does nothing, so maybe have it throw an exception? */ (
                        index: number,
                        f: ValueFilter<ArtifactKey>
                      ) => {
                        setFilter(index, f);
                      }
                    }
                    setTriggerScopeID={setTriggerScopeID}
                    triggerScopeID={triggerScopeID}
                    setTriggerArtifactCollectionName={
                      setTriggerArtifactCollectionName
                    }
                  />
                </S.InlineBlockActionLeft>
                <S.InlineBlockActionRight>
                  <TooltipDeprecated
                    inverted
                    size="mini"
                    content="Remove filter"
                    position="right center"
                    on="hover"
                    trigger={
                      <S.DeleteFilter>
                        <IconClose
                          data-test="filter-delete"
                          onClick={() => {
                            setIsShowArtifactFilterEnabled(false);
                            setTriggerScopeID(projectID);
                            if (setTriggerScopeType != null) {
                              setTriggerScopeType(TriggerScopeType.Project);
                              if (setTriggerArtifactCollectionName) {
                                setTriggerArtifactCollectionName('');
                              }
                            }

                            deleteFilter(1); // TODO: I'm pretty sure filter 1 is unused?
                          }}
                        />
                      </S.DeleteFilter>
                    }
                  />
                </S.InlineBlockActionRight>
              </S.InlineBlockAction>
            </>
          )}
        </>
      )}
    </>
  );
};

export const ArtifactOrCollectionTagFilterCreator = ({
  filters: rootFilters,
  artifactCollectionID,
  setFilter,
  deleteFilter,
  setTriggerScopeType,
  setTriggerScopeID,
  triggerScopeType,
  triggerScopeID,
  setTriggerArtifactCollectionName,
}: EventConfigurationProps) => {
  const {entryPoint, projectID, getAllowArtifactSelection} = useEventStore(
    useShallow(s => s)
  );
  const allowArtifactSelection = getAllowArtifactSelection();
  const isScopeFixed = isScopeFixedEntryPoint(entryPoint);
  const [isShowArtifactFilterEnabled, setIsShowArtifactFilterEnabled] =
    useState(
      allowArtifactSelection &&
        triggerScopeType === TriggerScopeType.ArtifactCollection
    );
  const tagRegexError = getRegexError(
    (rootFilters.filters[0]?.value as string) ?? ''
  );
  useEffect(() => {
    // The triggerScopeType/triggerScopeID must get set, so
    // when the user is not selecting a collection manually,
    // we need to set it for them.
    // When the scope is fixed (Hub picks it, or an org/team edit carries it),
    // it is set outside this UI and must not be re-derived here.
    if (
      !isScopeFixed &&
      (!isShowArtifactFilterEnabled || !allowArtifactSelection)
    ) {
      if (artifactCollectionID) {
        setTriggerScopeType?.(TriggerScopeType.ArtifactCollection);
        setTriggerScopeID(artifactCollectionID);
      } else {
        setTriggerScopeType?.(TriggerScopeType.Project);
        setTriggerScopeID(projectID);
      }
    }

    const firstFilterValue = rootFilters.filters[0]?.value;
    const firstFilterIsValid =
      firstFilterValue != null &&
      firstFilterValue !== '' &&
      getRegexError(firstFilterValue as string) === undefined;
    EventStoreActions.setIsAddTagConfigurationOk(
      isAliasOrTagEventConfiguredOk(
        firstFilterIsValid,
        allowArtifactSelection,
        isShowArtifactFilterEnabled,
        triggerScopeID
      )
    );
  }, [
    artifactCollectionID,
    allowArtifactSelection,
    isScopeFixed,
    isShowArtifactFilterEnabled,
    projectID,
    rootFilters.filters,
    setTriggerScopeID,
    setTriggerScopeType,
    triggerScopeID,
  ]);
  return (
    <>
      <S.LabelRow>
        <S.Label>Tag regex</S.Label>
      </S.LabelRow>
      <S.StyledTextField
        placeholder="Name or regex"
        value={(rootFilters.filters[0]?.value as string) ?? ''}
        onChange={value => {
          setFilter(0, createArtifactFilter('tag', value as string));
        }}
        autoFocus={true}
      />
      {tagRegexError && <ErrorText>{tagRegexError}</ErrorText>}

      {allowArtifactSelection && (
        <>
          {!isShowArtifactFilterEnabled && rootFilters.filters[0] && (
            <S.AddFilter
              className="fake-link filter__new"
              data-test="filter-add"
              onClick={() => {
                setIsShowArtifactFilterEnabled(true);
                if (setTriggerScopeType != null) {
                  setTriggerScopeType(TriggerScopeType.ArtifactCollection);
                  setTriggerScopeID(artifactCollectionID ?? '');
                }
              }}>
              Add artifact filter
            </S.AddFilter>
          )}
          {isShowArtifactFilterEnabled && (
            <>
              <S.LabelRow>
                <S.Label>Artifact</S.Label>
              </S.LabelRow>
              <S.InlineBlockAction>
                <S.InlineBlockActionLeft>
                  <ArtifactCollectionSelector
                    index={1}
                    descriptionString="Artifact"
                    filter={rootFilters.filters[1] ?? EMPTY_FILTER_VALUE}
                    setFilter={
                      /* TODO: it seems like this does nothing, so maybe have it throw an exception? */ (
                        index: number,
                        f: ValueFilter<ArtifactKey>
                      ) => {
                        setFilter(index, f);
                      }
                    }
                    setTriggerScopeID={setTriggerScopeID}
                    triggerScopeID={triggerScopeID}
                    setTriggerArtifactCollectionName={
                      setTriggerArtifactCollectionName
                    }
                  />
                </S.InlineBlockActionLeft>
                <S.InlineBlockActionRight>
                  <TooltipDeprecated
                    inverted
                    size="mini"
                    content="Remove filter"
                    position="right center"
                    on="hover"
                    trigger={
                      <S.DeleteFilter>
                        <IconClose
                          data-test="filter-delete"
                          onClick={() => {
                            setIsShowArtifactFilterEnabled(false);
                            setTriggerScopeID(projectID);
                            if (setTriggerScopeType != null) {
                              setTriggerScopeType(TriggerScopeType.Project);
                              if (setTriggerArtifactCollectionName) {
                                setTriggerArtifactCollectionName('');
                              }
                            }

                            deleteFilter(1); // TODO: I'm pretty sure filter 1 is unused?
                          }}
                        />
                      </S.DeleteFilter>
                    }
                  />
                </S.InlineBlockActionRight>
              </S.InlineBlockAction>
            </>
          )}
        </>
      )}
    </>
  );
};

export const ArtifactLinkFilterCreator = (props: EventConfigurationProps) => {
  // Right now, we don't have any additional configuration options for the link artifact filter.
  // We could potentially allow users to select a specific collection when you're in the registry
  // wide entry point, but if users want to do that, we want them to go to the collection entry point.

  // These variables lets us avoid having props in the useEffect dependency array.
  const {artifactCollectionID, setTriggerScopeID, setTriggerScopeType} = props;
  const {entryPoint, projectID} = useEventStore(useShallow(s => s));
  const isScopeFixed = isScopeFixedEntryPoint(entryPoint);
  useEffect(() => {
    // When the scope is fixed (Hub picks it, or an org/team edit carries it),
    // it is set outside this UI and must not be re-derived here.
    if (isScopeFixed) {
      return;
    }
    // Do first time setup of the trigger scope type and ID. Necessary when
    // the user isn't selecting a collection manually (eg project level scope,
    // or when the collection is specified by the entry point).
    // Future improvement: move this logic up into the eventConfig store.
    if (artifactCollectionID != null) {
      setTriggerScopeType?.(TriggerScopeType.ArtifactCollection);
      setTriggerScopeID(artifactCollectionID);
    } else {
      setTriggerScopeType?.(TriggerScopeType.Project);
      setTriggerScopeID(projectID);
    }
    // note that right now these are all non-changing values, so this should not
    // get triggered more than once per run of the wizard
  }, [
    artifactCollectionID,
    isScopeFixed,
    projectID,
    setTriggerScopeID,
    setTriggerScopeType,
  ]);

  return null;
};

export const CreateArtifactFilterCreator = (props: EventConfigurationProps) => {
  return (
    <>
      <S.LabelRow>
        <S.Label>Artifact</S.Label>
      </S.LabelRow>
      <ArtifactCollectionSelector
        {...props}
        index={0}
        setFilter={props.setFilter}
        filter={props.filters.filters[0]}
        descriptionString="Artifact"
        triggerScopeID={props.triggerScopeID}
        setTriggerScopeID={props.setTriggerScopeID}
        setTriggerArtifactCollectionName={
          props.setTriggerArtifactCollectionName
        }
      />
    </>
  );
};

export const ArtifactCollectionSelector = (
  props: EventConfigurationFilterProps
) => {
  const eventConfig = useEventStore(useShallow(s => s));
  const {data, loading} = useProjectArtifactCollectionsQuery({
    variables: {
      entityName: eventConfig.entityName ?? '',
      projectName: eventConfig.projectName ?? '',
      collectionTypes: [ArtifactCollectionType.Sequence],
    },
    skip: eventConfig.entityName == null || eventConfig.projectName == null,
    context: propagateErrorsContext(),
  });
  const setTriggerScopeID = props.setTriggerScopeID;
  const setTriggerArtifactCollectionName =
    props.setTriggerArtifactCollectionName;

  const artifactCollections: OptionType[] =
    data?.project?.artifactTypes.edges
      .map(({node: artifactType}) => {
        return artifactType?.artifactCollections?.edges.map(({node}) => {
          return {
            label: node?.name as string,
            value: node?.id as string,
          };
        });
      })
      .flat()
      .filter((v): v is OptionType => v != null) ?? [];

  useEffect(() => {
    if (setTriggerArtifactCollectionName && props.triggerScopeID) {
      setTriggerArtifactCollectionName(
        artifactCollections.find(
          option => option.value === props.triggerScopeID
        )?.label ?? ''
      );
    }
  });
  if (loading || data == null) {
    return <div></div>;
  }
  return (
    <Select<OptionType | null>
      placeholder={`Select your ${props.descriptionString.toLowerCase()}`}
      defaultValue={
        props.triggerScopeID
          ? {
              label:
                artifactCollections.find(
                  ac => ac.value === props.triggerScopeID
                )?.label ?? '',
              value: props.triggerScopeID,
            }
          : null
      }
      options={artifactCollections}
      onChange={(option: OptionType | null) => {
        if (option) {
          setTriggerScopeID(option.value);
          if (setTriggerArtifactCollectionName) {
            setTriggerArtifactCollectionName(option.label);
          }
        }
      }}
    />
  );
};
